import { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  Button,
  Box,
  CircularProgress,
  Fade,
  Alert,
} from "@mui/material";
import { useAppSelector, useAppDispatch } from "../../../../redux/store";

import { updateShippingAddress } from "../../../../redux/slices/projectsSlice";

// US States for the state select:
const US_STATES = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
];

// All countries according to ISO 3166-1 alpha-2 (ISO2) standard
const COUNTRIES = [
  { code: "AD", name: "Andorra" },
  { code: "AE", name: "United Arab Emirates" },
  { code: "AF", name: "Afghanistan" },
  { code: "AG", name: "Antigua and Barbuda" },
  { code: "AI", name: "Anguilla" },
  { code: "AL", name: "Albania" },
  { code: "AM", name: "Armenia" },
  { code: "AO", name: "Angola" },
  { code: "AQ", name: "Antarctica" },
  { code: "AR", name: "Argentina" },
  { code: "AS", name: "American Samoa" },
  { code: "AT", name: "Austria" },
  { code: "AU", name: "Australia" },
  { code: "AW", name: "Aruba" },
  { code: "AX", name: "Åland Islands" },
  { code: "AZ", name: "Azerbaijan" },
  { code: "BA", name: "Bosnia and Herzegovina" },
  { code: "BB", name: "Barbados" },
  { code: "BD", name: "Bangladesh" },
  { code: "BE", name: "Belgium" },
  { code: "BF", name: "Burkina Faso" },
  { code: "BG", name: "Bulgaria" },
  { code: "BH", name: "Bahrain" },
  { code: "BI", name: "Burundi" },
  { code: "BJ", name: "Benin" },
  { code: "BL", name: "Saint Barthélemy" },
  { code: "BM", name: "Bermuda" },
  { code: "BN", name: "Brunei Darussalam" },
  { code: "BO", name: "Bolivia" },
  { code: "BQ", name: "Bonaire, Sint Eustatius and Saba" },
  { code: "BR", name: "Brazil" },
  { code: "BS", name: "Bahamas" },
  { code: "BT", name: "Bhutan" },
  { code: "BV", name: "Bouvet Island" },
  { code: "BW", name: "Botswana" },
  { code: "BY", name: "Belarus" },
  { code: "BZ", name: "Belize" },
  { code: "CA", name: "Canada" },
  { code: "CC", name: "Cocos (Keeling) Islands" },
  { code: "CD", name: "Congo, Democratic Republic of the" },
  { code: "CF", name: "Central African Republic" },
  { code: "CG", name: "Congo" },
  { code: "CH", name: "Switzerland" },
  { code: "CI", name: "Côte d'Ivoire" },
  { code: "CK", name: "Cook Islands" },
  { code: "CL", name: "Chile" },
  { code: "CM", name: "Cameroon" },
  { code: "CN", name: "China" },
  { code: "CO", name: "Colombia" },
  { code: "CR", name: "Costa Rica" },
  { code: "CU", name: "Cuba" },
  { code: "CV", name: "Cabo Verde" },
  { code: "CW", name: "Curaçao" },
  { code: "CX", name: "Christmas Island" },
  { code: "CY", name: "Cyprus" },
  { code: "CZ", name: "Czechia" },
  { code: "DE", name: "Germany" },
  { code: "DJ", name: "Djibouti" },
  { code: "DK", name: "Denmark" },
  { code: "DM", name: "Dominica" },
  { code: "DO", name: "Dominican Republic" },
  { code: "DZ", name: "Algeria" },
  { code: "EC", name: "Ecuador" },
  { code: "EE", name: "Estonia" },
  { code: "EG", name: "Egypt" },
  { code: "EH", name: "Western Sahara" },
  { code: "ER", name: "Eritrea" },
  { code: "ES", name: "Spain" },
  { code: "ET", name: "Ethiopia" },
  { code: "FI", name: "Finland" },
  { code: "FJ", name: "Fiji" },
  { code: "FK", name: "Falkland Islands" },
  { code: "FM", name: "Micronesia" },
  { code: "FO", name: "Faroe Islands" },
  { code: "FR", name: "France" },
  { code: "GA", name: "Gabon" },
  { code: "GB", name: "United Kingdom" },
  { code: "GD", name: "Grenada" },
  { code: "GE", name: "Georgia" },
  { code: "GF", name: "French Guiana" },
  { code: "GG", name: "Guernsey" },
  { code: "GH", name: "Ghana" },
  { code: "GI", name: "Gibraltar" },
  { code: "GL", name: "Greenland" },
  { code: "GM", name: "Gambia" },
  { code: "GN", name: "Guinea" },
  { code: "GP", name: "Guadeloupe" },
  { code: "GQ", name: "Equatorial Guinea" },
  { code: "GR", name: "Greece" },
  { code: "GS", name: "South Georgia and the South Sandwich Islands" },
  { code: "GT", name: "Guatemala" },
  { code: "GU", name: "Guam" },
  { code: "GW", name: "Guinea-Bissau" },
  { code: "GY", name: "Guyana" },
  { code: "HK", name: "Hong Kong" },
  { code: "HM", name: "Heard Island and McDonald Islands" },
  { code: "HN", name: "Honduras" },
  { code: "HR", name: "Croatia" },
  { code: "HT", name: "Haiti" },
  { code: "HU", name: "Hungary" },
  { code: "ID", name: "Indonesia" },
  { code: "IE", name: "Ireland" },
  { code: "IL", name: "Israel" },
  { code: "IM", name: "Isle of Man" },
  { code: "IN", name: "India" },
  { code: "IO", name: "British Indian Ocean Territory" },
  { code: "IQ", name: "Iraq" },
  { code: "IR", name: "Iran" },
  { code: "IS", name: "Iceland" },
  { code: "IT", name: "Italy" },
  { code: "JE", name: "Jersey" },
  { code: "JM", name: "Jamaica" },
  { code: "JO", name: "Jordan" },
  { code: "JP", name: "Japan" },
  { code: "KE", name: "Kenya" },
  { code: "KG", name: "Kyrgyzstan" },
  { code: "KH", name: "Cambodia" },
  { code: "KI", name: "Kiribati" },
  { code: "KM", name: "Comoros" },
  { code: "KN", name: "Saint Kitts and Nevis" },
  { code: "KP", name: "North Korea" },
  { code: "KR", name: "South Korea" },
  { code: "KW", name: "Kuwait" },
  { code: "KY", name: "Cayman Islands" },
  { code: "KZ", name: "Kazakhstan" },
  { code: "LA", name: "Laos" },
  { code: "LB", name: "Lebanon" },
  { code: "LC", name: "Saint Lucia" },
  { code: "LI", name: "Liechtenstein" },
  { code: "LK", name: "Sri Lanka" },
  { code: "LR", name: "Liberia" },
  { code: "LS", name: "Lesotho" },
  { code: "LT", name: "Lithuania" },
  { code: "LU", name: "Luxembourg" },
  { code: "LV", name: "Latvia" },
  { code: "LY", name: "Libya" },
  { code: "MA", name: "Morocco" },
  { code: "MC", name: "Monaco" },
  { code: "MD", name: "Moldova" },
  { code: "ME", name: "Montenegro" },
  { code: "MF", name: "Saint Martin" },
  { code: "MG", name: "Madagascar" },
  { code: "MH", name: "Marshall Islands" },
  { code: "MK", name: "North Macedonia" },
  { code: "ML", name: "Mali" },
  { code: "MM", name: "Myanmar" },
  { code: "MN", name: "Mongolia" },
  { code: "MO", name: "Macao" },
  { code: "MP", name: "Northern Mariana Islands" },
  { code: "MQ", name: "Martinique" },
  { code: "MR", name: "Mauritania" },
  { code: "MS", name: "Montserrat" },
  { code: "MT", name: "Malta" },
  { code: "MU", name: "Mauritius" },
  { code: "MV", name: "Maldives" },
  { code: "MW", name: "Malawi" },
  { code: "MX", name: "Mexico" },
  { code: "MY", name: "Malaysia" },
  { code: "MZ", name: "Mozambique" },
  { code: "NA", name: "Namibia" },
  { code: "NC", name: "New Caledonia" },
  { code: "NE", name: "Niger" },
  { code: "NF", name: "Norfolk Island" },
  { code: "NG", name: "Nigeria" },
  { code: "NI", name: "Nicaragua" },
  { code: "NL", name: "Netherlands" },
  { code: "NO", name: "Norway" },
  { code: "NP", name: "Nepal" },
  { code: "NR", name: "Nauru" },
  { code: "NU", name: "Niue" },
  { code: "NZ", name: "New Zealand" },
  { code: "OM", name: "Oman" },
  { code: "PA", name: "Panama" },
  { code: "PE", name: "Peru" },
  { code: "PF", name: "French Polynesia" },
  { code: "PG", name: "Papua New Guinea" },
  { code: "PH", name: "Philippines" },
  { code: "PK", name: "Pakistan" },
  { code: "PL", name: "Poland" },
  { code: "PM", name: "Saint Pierre and Miquelon" },
  { code: "PN", name: "Pitcairn" },
  { code: "PR", name: "Puerto Rico" },
  { code: "PS", name: "Palestine" },
  { code: "PT", name: "Portugal" },
  { code: "PW", name: "Palau" },
  { code: "PY", name: "Paraguay" },
  { code: "QA", name: "Qatar" },
  { code: "RE", name: "Réunion" },
  { code: "RO", name: "Romania" },
  { code: "RS", name: "Serbia" },
  { code: "RU", name: "Russian Federation" },
  { code: "RW", name: "Rwanda" },
  { code: "SA", name: "Saudi Arabia" },
  { code: "SB", name: "Solomon Islands" },
  { code: "SC", name: "Seychelles" },
  { code: "SD", name: "Sudan" },
  { code: "SE", name: "Sweden" },
  { code: "SG", name: "Singapore" },
  { code: "SH", name: "Saint Helena" },
  { code: "SI", name: "Slovenia" },
  { code: "SJ", name: "Svalbard and Jan Mayen" },
  { code: "SK", name: "Slovakia" },
  { code: "SL", name: "Sierra Leone" },
  { code: "SM", name: "San Marino" },
  { code: "SN", name: "Senegal" },
  { code: "SO", name: "Somalia" },
  { code: "SR", name: "Suriname" },
  { code: "SS", name: "South Sudan" },
  { code: "ST", name: "Sao Tome and Principe" },
  { code: "SV", name: "El Salvador" },
  { code: "SX", name: "Sint Maarten" },
  { code: "SY", name: "Syrian Arab Republic" },
  { code: "SZ", name: "Eswatini" },
  { code: "TC", name: "Turks and Caicos Islands" },
  { code: "TD", name: "Chad" },
  { code: "TF", name: "French Southern Territories" },
  { code: "TG", name: "Togo" },
  { code: "TH", name: "Thailand" },
  { code: "TJ", name: "Tajikistan" },
  { code: "TK", name: "Tokelau" },
  { code: "TL", name: "Timor-Leste" },
  { code: "TM", name: "Turkmenistan" },
  { code: "TN", name: "Tunisia" },
  { code: "TO", name: "Tonga" },
  { code: "TR", name: "Turkey" },
  { code: "TT", name: "Trinidad and Tobago" },
  { code: "TV", name: "Tuvalu" },
  { code: "TW", name: "Taiwan" },
  { code: "TZ", name: "Tanzania" },
  { code: "UA", name: "Ukraine" },
  { code: "UG", name: "Uganda" },
  { code: "UM", name: "United States Minor Outlying Islands" },
  { code: "US", name: "United States" },
  { code: "UY", name: "Uruguay" },
  { code: "UZ", name: "Uzbekistan" },
  { code: "VA", name: "Holy See" },
  { code: "VC", name: "Saint Vincent and the Grenadines" },
  { code: "VE", name: "Venezuela" },
  { code: "VG", name: "Virgin Islands, British" },
  { code: "VI", name: "Virgin Islands, U.S." },
  { code: "VN", name: "Viet Nam" },
  { code: "VU", name: "Vanuatu" },
  { code: "WF", name: "Wallis and Futuna" },
  { code: "WS", name: "Samoa" },
  { code: "YE", name: "Yemen" },
  { code: "YT", name: "Mayotte" },
  { code: "ZA", name: "South Africa" },
  { code: "ZM", name: "Zambia" },
  { code: "ZW", name: "Zimbabwe" },
];

const UpdateShippingAddressDialog = ({
  open,
  onClose,
  lineItemData,
  projectId,
}: any) => {
  const [fadeIn, setFadeIn] = useState(false);

  const {
    updateProjectShippingAddressStatus,
    updateProjectShippingAddressError,
    fetchProjectsStatus,
  } = useAppSelector((state) => state.projects) as any;
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (open) {
      setFadeIn(true);
    } else {
      setFadeIn(false);
    }
  }, [open]);

  const [formData, setFormData] = useState({
    shipAddress1: "",
    shipAddress2: "",
    shipAddressCity: "",
    shipAddressState: "",
    shipAddressZip: "",
    shipAddressCountry: "US",
  });

  // Reset form when dialog opens with new line item data
  useEffect(() => {
    if (open && lineItemData) {
      if (lineItemData.shippingAddress) {
        setFormData({
          shipAddress1: lineItemData.shippingAddress.address1 || "",
          shipAddress2: "",
          shipAddressCity: lineItemData.shippingAddress.city || "",
          shipAddressState: lineItemData.shippingAddress.state || "",
          shipAddressZip: lineItemData.shippingAddress.zip || "",
          shipAddressCountry: lineItemData.shippingAddress.country || "US",
        });
      } else {
        setFormData({
          shipAddress1:
            lineItemData.customerAddress?.split(",")[0]?.trim() || "",
          shipAddress2: "",
          shipAddressCity:
            lineItemData.customerAddress?.split(",")[1]?.trim() || "",
          shipAddressState:
            lineItemData.customerAddress?.split(",")[2]?.trim() || "",
          shipAddressZip: "",
          shipAddressCountry: "US",
        });
      }
    }
  }, [open, lineItemData]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setFormData({
        shipAddress1: "",
        shipAddress2: "",
        shipAddressCity: "",
        shipAddressState: "",
        shipAddressZip: "",
        shipAddressCountry: "US",
      });
    }
  }, [open]);

  const handleFieldChange = (field: any) => (event: any) => {
    const value = event.target.value;
    const updatedData = {
      ...formData,
      [field]: value,
    };
    setFormData(updatedData);
  };

  const handleSave = () => {
    dispatch(
      updateShippingAddress({
        projectId,
        lineItemId: lineItemData.id,
        shippingAddress: formData,
      })
    );
  };

  const handleCancel = () => {
    // Start fade out animation
    setFadeIn(false);

    // Delay the actual cancel action to allow fade out to complete
    setTimeout(() => {
      onClose();
    }, 500); // Match the fade timeout
  };

  const isFormValid =
    formData.shipAddress1.trim() &&
    formData.shipAddressCity.trim() &&
    formData.shipAddressState.trim() &&
    formData.shipAddressZip.trim() &&
    formData.shipAddressCountry.trim();

  const loading = useMemo(
    () =>
      updateProjectShippingAddressStatus === "loading" ||
      fetchProjectsStatus === "loading",
    [updateProjectShippingAddressStatus, fetchProjectsStatus]
  );

  // Close dialog on successful update
  useEffect(() => {
    if (updateProjectShippingAddressStatus === "succeeded") {
      onClose();
    }
  }, [updateProjectShippingAddressStatus, onClose]);

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <Fade in={fadeIn}>
        <Card
          sx={{
            borderRadius: 0,
            backgroundColor: "#f7f7f7",
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2 }}>
              Update Shipping Address
            </Typography>
            {lineItemData && (
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                Line Item: {lineItemData.customerName || "N/A"}
              </Typography>
            )}

            {updateProjectShippingAddressError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {updateProjectShippingAddressError}
              </Alert>
            )}

            <Grid container rowSpacing={2}>
              {/* Address Line 1 */}
              <Grid size={12}>
                <InputLabel
                  htmlFor="shipAddress1"
                  sx={{ fontSize: 14, mb: 0.5 }}
                >
                  Address Line 1 *
                </InputLabel>
                <TextField
                  id="shipAddress1"
                  name="shipAddress1"
                  variant="outlined"
                  size="small"
                  fullWidth
                  disabled={loading}
                  error={!!updateProjectShippingAddressError}
                  placeholder="Enter street address"
                  value={formData.shipAddress1}
                  onChange={handleFieldChange("shipAddress1")}
                  autoComplete="address-line1"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 0,
                    },
                  }}
                />
              </Grid>

              {/* Address Line 2 */}
              <Grid size={12}>
                <InputLabel
                  htmlFor="shipAddress2"
                  sx={{ fontSize: 14, mb: 0.5 }}
                >
                  Address Line 2 (Optional)
                </InputLabel>
                <TextField
                  id="shipAddress2"
                  name="shipAddress2"
                  variant="outlined"
                  size="small"
                  fullWidth
                  disabled={loading}
                  placeholder="Apartment, suite, unit, building, floor, etc."
                  value={formData.shipAddress2}
                  onChange={handleFieldChange("shipAddress2")}
                  autoComplete="address-line2"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 0,
                    },
                  }}
                />
              </Grid>

              {/* City */}
              <Grid size={12}>
                <InputLabel
                  htmlFor="shipAddressCity"
                  sx={{ fontSize: 14, mb: 0.5 }}
                >
                  City *
                </InputLabel>
                <TextField
                  id="shipAddressCity"
                  name="shipAddressCity"
                  variant="outlined"
                  size="small"
                  fullWidth
                  disabled={loading}
                  error={!!updateProjectShippingAddressError}
                  placeholder="Enter city"
                  value={formData.shipAddressCity}
                  onChange={handleFieldChange("shipAddressCity")}
                  autoComplete="address-level2"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 0,
                    },
                  }}
                />
              </Grid>

              {/* ZIP Code */}
              <Grid size={12}>
                <InputLabel
                  htmlFor="shipAddressZip"
                  sx={{ fontSize: 14, mb: 0.5 }}
                >
                  ZIP/Postal Code *
                </InputLabel>
                <TextField
                  id="shipAddressZip"
                  name="shipAddressZip"
                  variant="outlined"
                  size="small"
                  fullWidth
                  disabled={loading}
                  error={!!updateProjectShippingAddressError}
                  placeholder="Enter ZIP code"
                  value={formData.shipAddressZip}
                  onChange={handleFieldChange("shipAddressZip")}
                  autoComplete="postal-code"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 0,
                    },
                  }}
                />
              </Grid>

              {/* Country */}
              <Grid size={12}>
                <InputLabel
                  htmlFor="shipAddressCountry"
                  sx={{ fontSize: 14, mb: 0.5 }}
                >
                  Country *
                </InputLabel>
                <FormControl fullWidth size="small">
                  <Select
                    id="shipAddressCountry"
                    name="shipAddressCountry"
                    value={formData.shipAddressCountry}
                    onChange={handleFieldChange("shipAddressCountry")}
                    disabled={loading}
                    error={!!updateProjectShippingAddressError}
                    autoComplete="country"
                    sx={{
                      borderRadius: 0,
                    }}
                  >
                    {COUNTRIES.map((country) => (
                      <MenuItem key={country.code} value={country.code}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* State */}
              <Grid size={12}>
                <InputLabel
                  htmlFor="shipAddressState"
                  sx={{ fontSize: 14, mb: 0.5 }}
                >
                  State/Province *
                </InputLabel>
                <FormControl fullWidth size="small">
                  <Select
                    id="shipAddressState"
                    name="shipAddressState"
                    value={formData.shipAddressState}
                    onChange={handleFieldChange("shipAddressState")}
                    disabled={loading}
                    error={!!updateProjectShippingAddressError}
                    autoComplete="address-level1"
                    sx={{
                      borderRadius: 0,
                    }}
                  >
                    {formData.shipAddressCountry === "US" ? (
                      US_STATES.map((state) => (
                        <MenuItem key={state.code} value={state.code}>
                          {state.name}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="">
                        <em>Enter manually for non-US addresses</em>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
                {formData.shipAddressCountry !== "US" && (
                  <TextField
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={loading}
                    error={!!updateProjectShippingAddressError}
                    placeholder="Enter state/province"
                    value={formData.shipAddressState}
                    onChange={handleFieldChange("shipAddressState")}
                    sx={{
                      mt: 1,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 0,
                        mt: 1,
                      },
                    }}
                  />
                )}
              </Grid>

              {/* Action Buttons */}
              <Grid size={12}>
                <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    disabled={loading}
                    size="small"
                    sx={{
                      borderRadius: 0,
                      flex: 1,
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={loading || !isFormValid}
                    size="small"
                    sx={{
                      borderRadius: 0,
                      flex: 1,
                    }}
                    startIcon={
                      loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : null
                    }
                  >
                    {loading ? "Saving..." : "Save Address"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Fade>
    </Dialog>
  );
};

export default UpdateShippingAddressDialog;
